import React, { Fragment } from 'react';
import { LogRow } from 'namirasoft-log';
import { IEntityInfo, NSARouterMakerResult } from 'namirasoft-account-react';
import { FilterItem } from 'namirasoft-core';
import { NSLoading, NSNoData, NSSpace, NSSpaceSizeType } from 'namirasoft-site-react';

import { LogProps } from '../LogProps';
import Styles from "./ConsoleView.module.css";

interface ConsoleViewProps extends NSARouterMakerResult, LogProps
{
	entity: IEntityInfo<LogRow, LogRow>;
	filters: FilterItem[];
}
interface ConsoleViewState
{
	logs: LogRow[] | null;
	loading: boolean;
}

class ConsoleView extends React.Component<ConsoleViewProps, ConsoleViewState>
{
	private LoaderNode: HTMLDivElement | null = null;
	private Observer: IntersectionObserver | null = null;
	private Generation: number = 0;
	private CurrentPage: number = 1;
	private TotalPages: number = 1;
	private PerPage: number = 100;

	static getColor(level: string)
	{
		if (level.toLowerCase() === "Log".toLowerCase())
			return "white";
		if (level.toLowerCase() === "Info".toLowerCase())
			return "cyan";
		if (level.toLowerCase() === "Trace".toLowerCase())
			return "blue";
		if (level.toLowerCase() === "Debug".toLowerCase())
			return "magenta";
		if (level.toLowerCase() === "Success".toLowerCase())
			return "green";
		if (level.toLowerCase() === "Warning".toLowerCase())
			return "yellow";
		if (level.toLowerCase() === "Error".toLowerCase())
			return "red";
		return "white";
	}

	static humanizeGap(deltaMs: number): string
	{
		const seconds = Math.floor(deltaMs / 1000);
		if (seconds < 60)
			return `${seconds} second${seconds === 1 ? "" : "s"} later`;
		const minutes = Math.floor(seconds / 60);
		if (minutes < 60)
			return `${minutes} minute${minutes === 1 ? "" : "s"} later`;
		const hours = Math.floor(minutes / 60);
		if (hours < 24)
			return `${hours} hour${hours === 1 ? "" : "s"} later`;
		const days = Math.floor(hours / 24);
		if (days < 30)
			return `${days} day${days === 1 ? "" : "s"} later`;
		const months = Math.floor(days / 30);
		if (months < 12)
			return `${months} month${months === 1 ? "" : "s"} later`;
		const years = Math.floor(months / 12);
		return `${years} year${years === 1 ? "" : "s"} later`;
	}

	static getSpaceSize(deltaMs: number): NSSpaceSizeType
	{
		if (deltaMs < 1000)
			return NSSpaceSizeType.SMALL;
		if (deltaMs < 60 * 1000)
			return NSSpaceSizeType.NORMAL;
		if (deltaMs < 60 * 60 * 1000)
			return NSSpaceSizeType.MEDUIM;
		return NSSpaceSizeType.LARGE;
	}

	constructor(props: ConsoleViewProps)
	{
		super(props);
		this.state = { logs: null, loading: false };

		if (typeof IntersectionObserver !== "undefined")
			this.Observer = new IntersectionObserver((entries) =>
			{
				if (entries[0].isIntersecting)
					this.loadMore();
			});

		this.setLoaderNode = this.setLoaderNode.bind(this);
	}

	private setLoaderNode(node: HTMLDivElement | null): void
	{
		if (this.LoaderNode)
			this.Observer?.unobserve(this.LoaderNode);
		this.LoaderNode = node;
		if (this.LoaderNode)
			this.Observer?.observe(this.LoaderNode);
	}

	private loadMore(): void
	{
		if (this.state.loading)
			return;
		if (this.CurrentPage > this.TotalPages)
			return;

		let generation = this.Generation;
		this.setState((prev) => ({ ...prev, loading: true }));
		this.props.entity.server.list?.(this.props.filters, this.CurrentPage, this.PerPage, [], null).then(res =>
		{
			if (generation !== this.Generation)
				return;
			this.setState(prev => ({ logs: [...(prev.logs ?? []), ...res.rows], loading: false }));
			this.TotalPages = Math.ceil(res.count / this.PerPage);
			this.CurrentPage++;
		}).catch(() =>
		{
			if (generation !== this.Generation)
				return;
			this.setState((prev) => ({ ...prev, loading: false }));
		});
	}

	override componentDidUpdate(prev_props: ConsoleViewProps): void
	{
		if (FilterItem.stringify(prev_props.filters) === FilterItem.stringify(this.props.filters))
			return;

		this.Generation++;
		this.CurrentPage = 1;
		this.TotalPages = 1;
		this.setState({ logs: null, loading: false }, () => this.loadMore());
	}

	override componentWillUnmount(): void
	{
		this.Generation++;
		this.Observer?.disconnect();
		this.Observer = null;
		this.LoaderNode = null;
	}

	renderGap(deltaMs: number): React.ReactNode
	{
		if (deltaMs < 60 * 1000)
			return <NSSpace size={ConsoleView.getSpaceSize(deltaMs)} />;

		return (
			<div className={Styles.gap_separator}>
				<div className={Styles.gap_line} />
				<span className={Styles.gap_label}>
					<span className={Styles.gap_dot} />
					{ConsoleView.humanizeGap(deltaMs)}
				</span>
				<div className={Styles.gap_line} />
			</div>
		);
	}

	override render(): React.ReactNode
	{
		if (this.state.logs?.length === 0)
			return (
				<NSNoData
					lable='No Log was found'
					style={{ background: "black", paddingBottom: "1rem" }}
				/>
			);

		return (
			<div style={{ backgroundColor: "black" }}>
				<NSSpace size={NSSpaceSizeType.MEDUIM} />
				{
					this.state.logs?.map((log, index) =>
					{
						const prev = index > 0 ? this.state.logs![index - 1] : null;
						const deltaMs = prev
							? Math.abs(new Date(log.created_at).getTime() - new Date(prev.created_at).getTime())
							: 0;

						return (
							<Fragment key={log.id}>
								{prev && this.renderGap(deltaMs)}
								<pre
									style={{
										color: ConsoleView.getColor(log?.level ?? ""),
										textWrap: "wrap"
									}}
								>
									{log.message}
								</pre>
							</Fragment>
						);
					})
				}

				<div ref={this.setLoaderNode} style={{ height: "1rem" }} />

				{
					(!this.state.logs || this.state.loading) &&
					(
						<NSLoading classList={[Styles.ns_loading]} hideTitle />
					)
				}
			</div>
		);
	}
}

export default ConsoleView;